import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

type AppEnv = typeof environment;

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private readonly apiUrl = environment?.apiUrl || '';

  public endpoints = {
    auth: {
      login: `${this.apiUrl}/auth/login`,
      loginWithProvider: `${this.apiUrl}/auth/login-with-provider`,
      forgotPassword: `${this.apiUrl}/auth/forgot-password`,
      validateResetPassword: `${this.apiUrl}/auth/validate-reset-password?token={{token}}&login={{login}}`,
      resetPassword: `${this.apiUrl}/auth/reset-password`,
      changePassword: `${this.apiUrl}/auth/change-password`,
      saveLoginDate: `${this.apiUrl}/auth/save-login-date`,
    },
    adBanners: {
      getAdBanners: `${this.apiUrl}/ad-banners`,
    },
    loginBackground: {
      get: `${this.apiUrl}/login-background`,
    },
    survey: {
      eligibility: `${this.apiUrl}/survey/eligibility`,
      event: `${this.apiUrl}/survey/event`,
    },
    categories: {
      getCategories: `${this.apiUrl}/categories`,
      getCategory: `${this.apiUrl}/categories/{{categoryId}}`,
    },
    customers: {
      getCustomers: `${this.apiUrl}/customers`,
      getCustomer: `${this.apiUrl}/customers/{{customerId}}`,
      setCustomer: `${this.apiUrl}/customers/set`,
      bankInfoByResidentId: `${this.apiUrl}/customers/{{customerId}}/resident/{{residentId}}/bank-info`,
      updateSepaEmailByResidentId: `${this.apiUrl}/customers/{{customerId}}/resident/{{residentId}}/bank-info/email`,
      checkIbanInUseByResidentId: `${this.apiUrl}/customers/{{customerId}}/resident/{{residentId}}/bank-info/iban-check`,
    },
    partnerSync: {
      getCustomerPartnerToken: `${this.apiUrl}/partner-sync/token`,
      updateConnextToken: `${this.apiUrl}/partner-sync/connext/token`,
      updateMedifoxToken: `${this.apiUrl}/partner-sync/medifox/token`,
      updatePartnerSync: `${this.apiUrl}/partner-sync/sync`,
      getPartnerDepartmentList: `${this.apiUrl}/partner-sync/departments`,
      updateDepartmentMapping: `${this.apiUrl}/partner-sync/departments/icons/mapping`,
      getPartnerResidentList: `${this.apiUrl}/partner-sync/departments/{{partnerDepartmentId}}/residents`,
      getAllPartnerResidentList: `${this.apiUrl}/partner-sync/residents`,
      getDepartmentSyncStatus: `${this.apiUrl}/partner-sync/departments/sync-status`,
      updateResidentMapping: `${this.apiUrl}/partner-sync/departments/{{partnerDepartmentId}}/residents/mapping`,
      updateResidentAccountMapping: `${this.apiUrl}/partner-sync/residents/account-mapping`,
      getDepartmentSyncDate: `${this.apiUrl}/partner-sync/departments/sync-date`,
    },
    products: {
      getCategoryProducts: `${this.apiUrl}/categories/{{categoryId}}/products`,
      getProducts: `${this.apiUrl}/products`,
      getSubcategoryProducts: `${this.apiUrl}/categories/subcategory/products`,
      getProductsByIds: `${this.apiUrl}/products/get-by-ids`,
      productsInCart: `${this.apiUrl}/products/show-products-in-cart`,
      favouriteProducts: `${this.apiUrl}/products/resident/{{residentId}}`,
      partnerProducts: `${this.apiUrl}/categories-partner/{{categoryId}}/products`,
      productDetails: `${this.apiUrl}/products/{{productId}}`,
      updateResidentFavourites: `${this.apiUrl}/products/{{customerId}}/resident/{{residentId}}/favourites`,
    },
    departments: {
      departments: `${this.apiUrl}/departments`,
      departmentWithResidents: `${this.apiUrl}/departments/{{departmentId}}`,
      departmentsWithResidents: `${this.apiUrl}/departments/with-residents`,
    },
    residents: {
      getResident: `${this.apiUrl}/residents/{{residentId}}`,
      updateResident: `${this.apiUrl}/residents/{{residentId}}`,
      deleteResident: `${this.apiUrl}/residents/{{residentId}}`,
      createResident: `${this.apiUrl}/residents`,
      getResidentOrdersSum: `${this.apiUrl}/residents/orders-sum/{{customerId}}`,
    },
    orders: {
      getLastOrderedProducts: `${this.apiUrl}/orders/last/{{residentId}}`,
      getOrdersHistory: `${this.apiUrl}/orders`,
      createOrder: `${this.apiUrl}/orders`,
      createMultipleOrders: `${this.apiUrl}/orders/multiple`,
      updateOrderRTPUrl: `${this.apiUrl}/orders/{{poid}}`,
      open: {
        resident: `${this.apiUrl}/orders/open/resident/{{residentId}}`,
        department: `${this.apiUrl}/orders/open/department/{{departmentId}}`,
      },
      getShippingPackagesPrices: `${this.apiUrl}/orders/shipping-packages-prices`,
      getResidentOrders: `${this.apiUrl}/orders/resident-orders/{{residentId}}`,
      printOrderOverview: `${this.apiUrl}/orders/overview/{{departmentId}}/print`,
    },
    profile: {
      profile: `${this.apiUrl}/profile`,
      user: `${this.apiUrl}/profile/user`,
      token: `${this.apiUrl}/profile/partners/token`,
    },
    images: {
      uploadImage: `${this.apiUrl}/images/upload/{{imageType}}/{{id}}`,
      uploadResidentImage: `${this.apiUrl}/images/upload/resident/{{customerId}}/{{departmentId}}/{{residentId}}`,
      deleteImage: `${this.apiUrl}/images/delete/{{imageType}}/{{id}}`,
      deleteResidentImage: `${this.apiUrl}/images/delete/resident/{{customerId}}/{{departmentId}}/{{residentId}}`,
    },
    downloads: {
      bedarf: `${this.apiUrl}/pdf/customer/{{customerId}}/bedarf`,
      sortiment: `${this.apiUrl}/pdf/customer/{{customerId}}/sortiment`,
      invoice: `${this.apiUrl}/pdf/order/{{orderId}}/invoice/{{invoiceNumber}}`,
      creditNote: `${this.apiUrl}/pdf/order/credit/{{orderId}}/invoice/summary`,
      getCreditResidentInvoicePp: `${this.apiUrl}/pdf/order/credit/{{orderId}}/invoice/pp/{{residentId}}`,
      creditNoteInvoicePP: `${this.apiUrl}/pdf/order/credit/{{orderId}}/invoice/pp`,
      invoicePP: `${this.apiUrl}/pdf/order/{{orderId}}/invoice/pp/{{invoiceNumber}}`,
      invoicePPZip: `${this.apiUrl}/pdf/order/{{orderId}}/invoice/pp/zip`,
      avisCsv: `${this.apiUrl}/pdf/order/{{orderId}}/residents/{{paymentMethod}}`,
      invoiceResident: `${this.apiUrl}/pdf/order/{{orderId}}/invoice/pp/{{subOrderId}}/{{residentId}}/{{residentName}}`,
      pickupList: `${this.apiUrl}/pdf/order/{{orderId}}/{{pickupListId}}`,
      sepaForm: `${this.apiUrl}/pdf/customer/{{customerId}}/sepa-form`,
    },
    cart: {
      cartUrl: `${this.apiUrl}/cart`,
      customerCartsUrl: `${this.apiUrl}/cart/all`,
      partnerCartUrl: `${this.apiUrl}/partner/cart`,
      getPackagesUrl: `${this.apiUrl}/cart/{{cartId}}/packages`,
    },
    suggestions: `${this.apiUrl}/suggestions`,
    logs: `${this.apiUrl}/logs`,
    search: {
      search: `${this.apiUrl}/search`,
      start: `${this.apiUrl}/search/start`,
      history: `${this.apiUrl}/search/history`,
    },
  };

  getEnvironment(): AppEnv {
    return environment;
  }

  isProd(): boolean {
    return environment.production;
  }

  getAPIUrl(): string {
    return this.apiUrl;
  }

  getAppName(): string {
    return environment.appName;
  }
}
